import type {
  AiModel,
  AiModelPrice,
  AiModelWithStats,
  AiUsageEvent,
  AiUsageRange,
  AiUsageSummary,
  AiUsageToken,
  AiUsageTokenWithStats,
  AlertEvent,
  AlertRule,
  Application,
  ApplicationEnvironment,
  ApplicationStatus,
  ApplicationWithStats,
  AuditRunDetail,
  AuditRunSummary,
  AuditTrigger,
  BenchmarkRunDetail,
  BenchmarkRunSummary,
  Condition,
  ConversationDetail,
  ConversationSummary,
  CreateAiToken,
  CreateAlertRule,
  CreateApplication,
  CreateMessageType,
  CreateUser,
  CreateVersionProduct,
  Log,
  LogWithRelations,
  MessageTypeWithStats,
  OverviewMetrics,
  PriceSyncRun,
  PushSubscribeBody,
  PushSubscriptionSummary,
  RailwayCostRange,
  RailwayCostsSummary,
  RailwayProject,
  RailwaySyncRunSummary,
  TestAlertRuleResponse,
  UnpricedModel,
  UpdateAiModel,
  UpdateAiToken,
  UpdateAlertRule,
  UpdateApplication,
  UpdateMessageType,
  UpdatePreferences,
  UpdateUser,
  UpdateVersionProduct,
  UpsertAiPrice,
  User,
  VersionProduct,
} from '@owl/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const ct = res.headers.get('content-type') ?? '';
  const body: unknown = ct.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    const msg =
      typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(msg, res.status);
  }
  return body as T;
}

type ListLogsParams = {
  applicationId?: string;
  applicationIds?: string[];
  messageTypeIds?: string[];
  environment?: ApplicationEnvironment;
  search?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
  metadata?: string;
};

function buildLogsQuery(p: ListLogsParams): string {
  const qs = new URLSearchParams();
  if (p.applicationId) qs.set('application_id', p.applicationId);
  if (p.applicationIds?.length) qs.set('application_ids', p.applicationIds.join(','));
  if (p.messageTypeIds?.length) qs.set('message_type_ids', p.messageTypeIds.join(','));
  if (p.environment) qs.set('environment', p.environment);
  if (p.search) qs.set('search', p.search);
  if (p.from) qs.set('from', p.from);
  if (p.to) qs.set('to', p.to);
  if (p.metadata) qs.set('metadata', p.metadata);
  if (p.cursor) qs.set('cursor', p.cursor);
  if (p.limit) qs.set('limit', String(p.limit));
  return qs.toString() ? `?${qs}` : '';
}

export const api = {
  // --- auth ---
  me: () => request<{ user: User }>('/admin/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: User }>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>('/admin/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/admin/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  updatePreferences: (patch: UpdatePreferences) =>
    request<{ user: User }>('/admin/auth/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // --- users (superadmin only) ---
  listUsers: () => request<{ items: User[] }>('/admin/users'),
  createUser: (body: CreateUser) =>
    request<{ user: User }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateUser: (id: string, patch: UpdateUser) =>
    request<{ user: User }>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteUser: (id: string) => request<{ ok: true }>(`/admin/users/${id}`, { method: 'DELETE' }),
  resetUserPassword: (id: string, newPassword: string) =>
    request<{ ok: true }>(`/admin/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),

  // --- applications ---
  listApplications: (environment?: ApplicationEnvironment) => {
    const qs = environment ? `?environment=${environment}` : '';
    return request<{ items: ApplicationWithStats[] }>(`/admin/applications${qs}`);
  },
  getApplication: (id: string) =>
    request<{ application: Application }>(`/admin/applications/${id}`),
  createApplication: (body: CreateApplication) =>
    request<{ application: Application; rawToken: string }>('/admin/applications', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateApplicationStatus: (id: string, status: ApplicationStatus) =>
    request<{ application: Application }>(`/admin/applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  updateApplicationEnvironment: (id: string, environment: ApplicationEnvironment) =>
    request<{ application: Application }>(`/admin/applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ environment }),
    }),
  updateApplication: (id: string, patch: UpdateApplication) =>
    request<{ application: Application }>(`/admin/applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteApplication: (id: string) =>
    request<{ ok: true }>(`/admin/applications/${id}`, { method: 'DELETE' }),
  regenerateToken: (id: string) =>
    request<{ application: Application; rawToken: string }>(
      `/admin/applications/${id}/regenerate-token`,
      { method: 'POST' },
    ),

  // --- message types ---
  listMessageTypes: () => request<{ items: MessageTypeWithStats[] }>('/admin/message-types'),
  createMessageType: (body: CreateMessageType) =>
    request<{ messageType: MessageTypeWithStats }>('/admin/message-types', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateMessageType: (id: string, body: UpdateMessageType) =>
    request<{ messageType: MessageTypeWithStats }>(`/admin/message-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteMessageType: (id: string, force = false) =>
    request<{ ok: true }>(`/admin/message-types/${id}${force ? '?force=true' : ''}`, {
      method: 'DELETE',
    }),

  // --- logs ---
  listLogs: (p: ListLogsParams = {}) =>
    request<{ items: LogWithRelations[]; nextCursor: string | null }>(
      `/admin/logs${buildLogsQuery(p)}`,
    ),
  getLog: (id: string) => request<{ log: LogWithRelations }>(`/admin/logs/${id}`),

  // --- alert rules ---
  listAlertRules: () => request<{ items: AlertRule[] }>('/admin/alert-rules'),
  getAlertRule: (id: string) => request<{ rule: AlertRule }>(`/admin/alert-rules/${id}`),
  createAlertRule: (body: CreateAlertRule) =>
    request<{ rule: AlertRule }>('/admin/alert-rules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateAlertRule: (id: string, body: UpdateAlertRule) =>
    request<{ rule: AlertRule }>(`/admin/alert-rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteAlertRule: (id: string) =>
    request<{ ok: true }>(`/admin/alert-rules/${id}`, { method: 'DELETE' }),
  testAlertRule: (id: string) =>
    request<TestAlertRuleResponse>(`/admin/alert-rules/${id}/test`, { method: 'POST' }),

  // --- alert events ---
  listAlertEvents: (
    p: {
      ruleId?: string;
      applicationId?: string;
      environment?: ApplicationEnvironment;
      limit?: number;
      cursor?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (p.ruleId) qs.set('rule_id', p.ruleId);
    if (p.applicationId) qs.set('application_id', p.applicationId);
    if (p.environment) qs.set('environment', p.environment);
    if (p.cursor) qs.set('cursor', p.cursor);
    if (p.limit) qs.set('limit', String(p.limit));
    return request<{ items: AlertEvent[]; nextCursor: string | null }>(
      `/admin/alert-events${qs.toString() ? `?${qs}` : ''}`,
    );
  },

  // --- metrics ---
  overview: (
    from: string,
    to: string,
    environment?: ApplicationEnvironment,
    applicationIds?: string[],
  ) => {
    const qs = new URLSearchParams({ from, to });
    if (environment) qs.set('environment', environment);
    if (applicationIds?.length) qs.set('application_ids', applicationIds.join(','));
    return request<OverviewMetrics>(`/admin/metrics/overview?${qs}`);
  },
  applicationMetrics: (id: string, from: string, to: string) => {
    const qs = new URLSearchParams({ from, to });
    return request<OverviewMetrics & { avgLatency: number | null; p95Latency: number | null }>(
      `/admin/metrics/application/${id}?${qs}`,
    );
  },

  // --- settings ---
  getSettings: () => request<{ settings: Record<string, unknown> }>('/admin/settings'),
  patchSettings: (body: Record<string, unknown>) =>
    request<{ settings: Record<string, unknown> }>('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // --- version products (per-product Control de versiones + Siembra) ---
  listVersionProducts: () => request<{ items: VersionProduct[] }>('/admin/version-products'),
  createVersionProduct: (body: CreateVersionProduct) =>
    request<{ product: VersionProduct }>('/admin/version-products', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateVersionProduct: (key: string, body: UpdateVersionProduct) =>
    request<{ product: VersionProduct }>(`/admin/version-products/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteVersionProduct: (key: string) =>
    request<{ ok: true }>(`/admin/version-products/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),
  // Live Siembra state for one product (resolves its Core repo server-side).
  getSiembra: (key: string) =>
    request<SiembraState>(`/admin/version-products/${encodeURIComponent(key)}/siembra`),
  triggerSiembra: (key: string, body: { tenants?: string; dryRun?: boolean }) =>
    request<{ ok: true }>(`/admin/version-products/${encodeURIComponent(key)}/siembra`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  setSiembraAuto: (key: string, enabled: boolean) =>
    request<{ autoEnabled: boolean }>(`/admin/version-products/${encodeURIComponent(key)}/auto`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  // --- AI usage: tokens ---
  listAiTokens: (params: { environment?: ApplicationEnvironment; applicationId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.environment) qs.set('environment', params.environment);
    if (params.applicationId) qs.set('applicationId', params.applicationId);
    return request<{ items: AiUsageTokenWithStats[] }>(
      `/admin/ai-tokens${qs.toString() ? `?${qs}` : ''}`,
    );
  },
  createAiToken: (body: CreateAiToken) =>
    request<{ token: AiUsageToken; rawToken: string }>('/admin/ai-tokens', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateAiToken: (id: string, patch: UpdateAiToken) =>
    request<{ token: AiUsageToken }>(`/admin/ai-tokens/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteAiToken: (id: string) =>
    request<{ ok: true }>(`/admin/ai-tokens/${id}`, { method: 'DELETE' }),
  regenerateAiToken: (id: string) =>
    request<{ token: AiUsageToken; rawToken: string }>(`/admin/ai-tokens/${id}/regenerate-token`, {
      method: 'POST',
    }),

  // --- AI usage: model catalog ---
  listAiModels: () => request<{ items: AiModelWithStats[] }>('/admin/ai-models'),
  updateAiModel: (id: string, body: UpdateAiModel) =>
    request<{ aiModel: AiModel }>(`/admin/ai-models/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // --- AI usage: prices ---
  listAiPrices: () => request<{ items: AiModelPrice[] }>('/admin/ai-prices'),
  upsertAiPrice: (body: UpsertAiPrice) =>
    request<{ price: AiModelPrice }>('/admin/ai-prices', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteAiPrice: (id: string) =>
    request<{ ok: true }>(`/admin/ai-prices/${id}`, { method: 'DELETE' }),
  syncAiPricesNow: () =>
    request<{
      runId: string;
      added: number;
      changed: number;
      unchanged: number;
      skippedLowerRank: number;
      errors: { provider?: string; model?: string; message: string }[];
    }>('/admin/ai-prices/sync', { method: 'POST' }),
  listAiPriceSyncRuns: (limit = 50) =>
    request<{ items: PriceSyncRun[] }>(`/admin/ai-prices/sync-runs?limit=${limit}`),
  listUnpricedModels: () => request<{ items: UnpricedModel[] }>('/admin/ai-prices/unpriced'),

  // --- AI usage: summary + events ---
  aiUsageSummary: (params: {
    range: AiUsageRange;
    applicationId?: string;
    provider?: string;
    model?: string;
    environment?: ApplicationEnvironment;
  }) => {
    const qs = new URLSearchParams({ range: params.range });
    if (params.applicationId) qs.set('applicationId', params.applicationId);
    if (params.provider) qs.set('provider', params.provider);
    if (params.model) qs.set('model', params.model);
    if (params.environment) qs.set('environment', params.environment);
    return request<AiUsageSummary>(`/admin/ai-usage/summary?${qs}`);
  },
  listAiUsageEvents: (params: {
    range: AiUsageRange;
    applicationId?: string;
    provider?: string;
    model?: string;
    environment?: ApplicationEnvironment;
    limit?: number;
  }) => {
    const qs = new URLSearchParams({ range: params.range });
    if (params.applicationId) qs.set('applicationId', params.applicationId);
    if (params.provider) qs.set('provider', params.provider);
    if (params.model) qs.set('model', params.model);
    if (params.environment) qs.set('environment', params.environment);
    if (params.limit) qs.set('limit', String(params.limit));
    return request<{ items: AiUsageEvent[] }>(`/admin/ai-usage/events?${qs}`);
  },

  // --- Railway costs ---
  railwayCostsSummary: (params: { range: RailwayCostRange; railwayProjectId?: string }) => {
    const qs = new URLSearchParams({ range: params.range });
    if (params.railwayProjectId) qs.set('railwayProjectId', params.railwayProjectId);
    return request<RailwayCostsSummary>(`/admin/railway-costs/summary?${qs}`);
  },
  listRailwayProjects: () => request<RailwayProject[]>('/admin/railway-costs/projects'),
  updateRailwayProject: (id: string, applicationId: string | null) =>
    request<RailwayProject>(`/admin/railway-costs/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ applicationId }),
    }),
  listRailwaySyncRuns: (limit = 20) =>
    request<RailwaySyncRunSummary[]>(`/admin/railway-costs/runs?limit=${limit}`),

  // --- Agent ---
  agentStatus: () => request<AgentStatusResponse>('/admin/agent/status'),
  agentBudget: () => request<AgentBudgetResponse>('/admin/agent/budget'),
  agentAudit: (
    params: {
      limit?: number;
      trigger?: 'discord_command' | 'scheduled' | 'alert_event' | 'manual';
      status?: 'ok' | 'error' | 'denied';
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.trigger) qs.set('trigger', params.trigger);
    if (params.status) qs.set('status', params.status);
    return request<{ entries: AgentAuditEntry[] }>(
      `/admin/agent/audit${qs.toString() ? `?${qs}` : ''}`,
    );
  },
  agentPatchSettings: (body: Partial<AgentSettingsPatch>) =>
    request<{ settings: AgentSettings }>('/admin/agent/settings', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  agentResetBreaker: () => request<{ ok: true }>('/admin/agent/reset-breaker', { method: 'POST' }),
  agentDailyReportRun: (dryRun: boolean) =>
    request<{
      ok: boolean;
      embed?: unknown;
      error?: string;
      costUsd?: number;
    }>('/admin/agent/daily-report/run', {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    }),
  agentNotify: (body: AgentNotifyBody) =>
    request<{ results: Array<{ channel: string; status: string; error?: string }> }>(
      '/admin/agent/notify',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  agentRegisterCommands: () =>
    request<{ ok: boolean; scope: 'guild' | 'global'; count?: number; error?: string }>(
      '/admin/agent/commands/register',
      { method: 'PATCH' },
    ),

  // --- Benchmark ---
  listBenchmarkRuns: (limit = 50) =>
    request<{ items: BenchmarkRunSummary[] }>(`/admin/benchmark/runs?limit=${limit}`),
  getBenchmarkRun: (id: string) => request<BenchmarkRunDetail>(`/admin/benchmark/runs/${id}`),

  // --- Conversations (real captured traffic) ---
  listConversations: (params?: {
    applicationId?: string;
    from?: string;
    to?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.applicationId) qs.set('application_id', params.applicationId);
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.limit) qs.set('limit', String(params.limit));
    const s = qs.toString();
    return request<{ items: ConversationSummary[] }>(`/admin/conversations${s ? `?${s}` : ''}`);
  },
  getConversation: (id: string) => request<ConversationDetail>(`/admin/conversations/${id}`),
  judgeConversation: (id: string) =>
    request<ConversationDetail>(`/admin/conversations/${id}/judge`, { method: 'POST' }),

  // --- Audits (bulk judge over real conversations) ---
  triggerAudit: (body: AuditTrigger) =>
    request<{ id: string; total: number }>('/admin/audits', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listAuditRuns: (limit = 50) =>
    request<{ items: AuditRunSummary[] }>(`/admin/audits?limit=${limit}`),
  getAuditRun: (id: string) => request<AuditRunDetail>(`/admin/audits/${id}`),

  // --- Web Push ---
  getPushPublicKey: () => request<{ publicKey: string | null }>('/admin/push/public-key'),
  listPushSubscriptions: () =>
    request<{ items: PushSubscriptionSummary[] }>('/admin/push/subscriptions'),
  subscribePush: (body: PushSubscribeBody) =>
    request<{ ok: true }>('/admin/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  unsubscribePush: (endpoint: string) =>
    request<{ ok: true }>('/admin/push/subscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
  testPush: (endpoint: string) =>
    request<{ ok: true }>('/admin/push/test', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),
};

export type SiembraRun = {
  id: number;
  status: string | null;
  conclusion: string | null;
  event: string;
  createdAt: string;
  url: string;
};

// Live Siembra state for a single version product. Core target is read from the
// file and key configured on that product (settings.py / RUNTIME_VERSION for the
// Python Cores, package.json / version for a TypeScript one). coreVersionStatus
// explains a null: 'not_found' = no such file (or a private repo the token
// cannot see), 'key_missing' = the file is there without that key,
// 'unauthorized' = the token is refused outright.
export type SiembraState = {
  configured: boolean;
  product: string;
  repo: string;
  autoEnabled: boolean;
  runs: SiembraRun[];
  coreVersion: string | null;
  coreVersionStatus: 'ok' | 'not_found' | 'key_missing' | 'unauthorized' | 'error' | 'unconfigured';
  // Repo names in the Core's .github/tenants.json. A tenant whose derived repo isn't
  // here would be skipped by the siembra Action. null = registry unreadable.
  registeredTenants: string[] | null;
};

export type AgentSettings = {
  agentEnabled: boolean;
  agentDailyReportEnabled: boolean;
  agentDailyReportHourUtc: number;
  agentDailyReportLastRunDate: string | null;
  agentAlertFormatEnabled: boolean;
  agentModelChat: string;
  agentModelReport: string;
  agentDiscordAdmins: string[];
  agentDailyBudgetUsd: number;
  agentMonthlyBudgetUsd: number;
  agentBudgetWarnedDate: string | null;
  agentMaxCallsPer5min: number;
  agentCircuitBreakerTrippedAt: string | null;
  agentInternalApplicationId: string | null;
};

export type AgentSettingsPatch = Omit<
  AgentSettings,
  | 'agentBudgetWarnedDate'
  | 'agentCircuitBreakerTrippedAt'
  | 'agentDailyReportLastRunDate'
  | 'agentInternalApplicationId'
>;

export type AgentStatusResponse = {
  env: {
    anthropicConfigured: boolean;
    discordBotConfigured: boolean;
    discordPublicKeyConfigured: boolean;
    discordApplicationIdConfigured: boolean;
    discordWebhookConfigured: boolean;
    discordGuildId: string | null;
  };
  settings: AgentSettings;
  breaker: { trippedAt: string | null; recentCalls: number; maxPer5min: number };
  budget: {
    today: number;
    month: number;
    dailyBudget: number;
    monthlyBudget: number;
    warnedToday: boolean;
    applicationId: string | null;
  };
};

export type AgentBudgetResponse = {
  today: number;
  month: number;
  dailyBudget: number;
  monthlyBudget: number;
  warnedDate: string | null;
  applicationId: string | null;
};

export type AgentAuditEntry = {
  id: string;
  timestamp: string;
  actor: string;
  trigger: 'discord_command' | 'scheduled' | 'alert_event' | 'manual';
  command: string | null;
  toolName: string | null;
  inputJson: unknown;
  outputSummary: string | null;
  status: 'ok' | 'error' | 'denied';
  errorMessage: string | null;
  discordUserId: string | null;
  latencyMs: number | null;
  costUsd: string | null;
};

export type AgentNotifyBody = {
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  applicationId?: string;
  formatWithLlm: boolean;
  channels: { discord: boolean; email: string[] };
};

export type {
  AiModel,
  AiModelPrice,
  AiModelWithStats,
  AiUsageEvent,
  AiUsageRange,
  AiUsageSummary,
  AiUsageToken,
  AiUsageTokenWithStats,
  Application,
  AlertEvent,
  AlertRule,
  ApplicationEnvironment,
  ApplicationWithStats,
  Condition,
  Log,
  LogWithRelations,
  MessageTypeWithStats,
  PriceSyncRun,
  UnpricedModel,
  User,
};
